import {
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import {
  NonNullableOrganisationState,
  NonNullableRecognitionApplicationState,
} from "../../../redux/state";
import { ApplicationAppBar } from "../ApplicationAppBar";
import { orgsService } from "../../../services";
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "../../../redux/selectors";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

type ApplicationProps = {
  orgState: NonNullableOrganisationState;
  applicationState: NonNullableRecognitionApplicationState;
  canEdit: boolean;
};

const criteriaB1TrueValue =
  "On behalf of the Applicant I declare that I have made diligent enquiry, including having had regard to the matters specified in this criterion, and have identified no concerns relating to the integrity of the Applicant. I am aware that Ofqual may conduct its own enquiries in this regard and should it discover I have provided false information this may cause it to reject the application for recognition or, should such information be discovered following recognition, to take enforcement action.";
const criteriaB1FalseValue =
  "Having made diligent enquiry, I have identified matters likely to of concern in respect of the integrity of the applicant and I provide full details of those concerns and an explanation how the attendant risks will be mitigated with the application.";

const criteriaB2TrueValue =
  "On behalf of the Applicant I declare that I have made diligent enquiry, including having had regard to the matters specified in this criterion, and have identified no concerns relating to the integrity of the proposed Senior Officers. I am aware that Ofqual may conduct its own enquiries in this regard and should it discover I have provided false information this may cause it to reject the application for recognition or, should such information be discovered following recognition, to take enforcement action.";
const criteriaB2FalseValue =
  "Having made diligent enquiry, I have identified matters likely to of concern in respect of the integrity of one or more of the proposed Senior Officers and I provide full details of those concerns and an explanation how the attendant risks will be mitigated with the application.";

export const Application: React.FC<ApplicationProps> = ({
  orgState,
  applicationState,
  canEdit,
}) => {
  const classes = useStyles();
  const { application } = applicationState;

  const { token } = useSelector(authSelector);
  const criteriaB1Value = application.criteriaB1
    ? criteriaB1TrueValue
    : criteriaB1FalseValue;
  const criteriaB2Value = application.criteriaB2
    ? criteriaB2TrueValue
    : criteriaB2FalseValue;

  const downloadBlob = useCallback(
    (blobId: string) =>
      orgsService.downloadApplicationAttachment(
        orgState.org.id,
        application.id,
        blobId,
        { token }
      ),
    [application.id, orgState.org.id, token]
  );

  return (
    <>
      <ApplicationAppBar
        orgState={orgState}
        applicationState={applicationState}
        tabName="Application"
        canEdit={canEdit}
      />
      <Typography variant="h6">Type of Qualifications</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Qualifications, EPAs or description of qualifications the organisation wishes to offer"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.qualificationDetails}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Explanation of why the organisation wants to be recognised by Ofqual to offer regulated qualifications"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.explanationOnRecognised}
            />
          </Grid>
        </Grid>
      </Paper>
      <Typography variant="h6">
        Criteria A: Identity, constitution and governance
      </Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="A.4 Organisation and governance"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.criteriaA4}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="A.5 Conflicts of interest"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.criteriaA5}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="A.6 Governing Body oversight"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.criteriaA6}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6">Criteria B : Integrity </Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* <FormControlLabel
              control={<Checkbox checked={application.criteriaB1} readOnly />}
              label="B.1"
            /> */}
            <TextField
              variant="outlined"
              label="B.1 Integrity of the applicant"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={criteriaB1Value}
            />
          </Grid>
          <Grid item xs={12}>
            {/* <FormControlLabel
              control={<Checkbox checked={application.criteriaB2} readOnly />}
              label="B.2"
            /> */}
            <TextField
              variant="outlined"
              label="B.2 Integrity of Senior Officers"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={criteriaB2Value}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6">Criteria C</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="C.1a"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.criteriaC1a}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="C.1b"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.criteriaC1b}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6">Criteria D</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="D.1a"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.competentD1a}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="D.1b"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.competentD1b}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="D.1c"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              readOnly
              value={application.competentD1c}
            />
          </Grid>
        </Grid>
      </Paper>
      <Typography variant="h6">Other Details</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox checked={application.readAndUnderstood} readOnly />
              }
              label="Read and understood"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Number of documents"
              fullWidth
              readOnly
              value={application.numberOfDocuments}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={application.section8a} readOnly />}
              label="Do you agree to us contacting other bodies to whose regulation you
              or your senior officers are subject?"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={application.section8b} readOnly />}
              label="Do you agree to us contacting other organisations named in your 
              application or supporting materials to verify the information you 
              have provided?"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Proposed responsible officer"
              fullWidth
              readOnly
              value={application.proposedResponsibleOfficer}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Submitted Date"
              fullWidth
              readOnly
              value={formatUtcDate(
                application.submittedDate,
                standardDateFormats.DATE_TIME
              )}
            />
          </Grid>
        </Grid>
      </Paper>
      <Typography variant="h6">Attachments</Typography>

      <Paper className={classes.formSection}>
        <ul
          style={{
            // temp styling
            listStyleType: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {application.attachments.map((attachment) => (
            <li key={attachment.id}>
              <DownloadLink
                fileName={attachment.fileName}
                getBlob={() => downloadBlob(attachment.id)}
              />
            </li>
          ))}
        </ul>
      </Paper>
    </>
  );
};
